/**
 * ScreenShareIndicator
 *
 * The UI that tells people *what* is being shared:
 *   - <ScreenShareIcon />        → the shared monitor glyph
 *   - <LocalScreenShareBanner /> → persistent "you're sharing X" banner + stop
 *   - <LocalSharePreview />      → opt-in thumbnail of the captured surface
 *
 * The browser only exposes the surface kind (screen / window / tab) for the
 * *local* capture, so remote viewers get the presenter's name instead.
 * State lives in `@/features/call/hooks/useScreenShare`.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocalScreenShare } from "@/features/call/hooks/useScreenShare";

/** Monitor glyph, shared by every screen-share affordance */
export const ScreenShareIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

/**
 * Persistent banner shown to the person sharing, so they always know which
 * surface is live — including when the call itself is scrolled past.
 */
export const LocalScreenShareBanner = () => {
  const { t } = useTranslation();
  const { isSharing, sourceLabel, stop } = useLocalScreenShare();
  if (!isSharing) return null;

  return (
    <div className="absolute top-4 left-4 z-30 flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/90 backdrop-blur-sm shadow-lg max-w-[60%]">
      <ScreenShareIcon className="w-4 h-4 text-white shrink-0" />
      <span className="text-white text-xs font-medium truncate">
        {t("call.you_are_sharing", { source: sourceLabel })}
      </span>
      <button
        onClick={stop}
        className="shrink-0 px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-[11px] font-semibold transition-colors"
      >
        {t("call.stop_sharing")}
      </button>
    </div>
  );
};

/**
 * Live thumbnail of the captured surface. Rendered from the local MediaStream
 * rather than the SDK's <Video>, and kept small + opt-in: showing the capture
 * back to the sharer at full size is what causes the infinite-mirror effect
 * when "Entire screen" is selected.
 */
export const LocalSharePreview = ({ stream }: { stream?: MediaStream }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream ?? null;
    return () => {
      el.srcObject = null;
    };
  }, [stream]);

  if (!stream) return null;

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="w-48 aspect-video rounded-lg border border-white/15 bg-black object-contain"
    />
  );
};
